/**
 * A deep link, turned into the same `data` dictionary a push carries. Pure:
 * no router, no native, no React — `route.ts`'s neighbour on purpose, so the
 * one mapping table cannot drift into two.
 *
 * The format, which `docs/DEVICE.md` and every flavor's `custom_url_scheme`
 * share (D96):
 *
 *     <custom_url_scheme>://open?screen=<screen>[&sessionId=<id>]
 *
 * The query parameters ARE the push `data`, key for key, so a link and a
 * tapped push route through {@link pushRoute} unchanged. Examples:
 * `app.mypocketgym.dev://open?screen=today`,
 * `app.mypocketgym.dev://open?screen=session-summary&sessionId=01J…`.
 *
 * ── WHY THE KEYS ARE AN ALLOW-LIST, NOT A PASSTHROUGH ──────────────────────
 * Only the keys `pushRoute` reads survive, and every other host, path and
 * parameter is dropped — which lands on Today, the home screen D96 names.
 * This is deliberately narrow: the draft review GENERATES a plan draft from
 * `sheet=draft` plus `protocol`/`exclude` in the URL (D150, `T-145`), so a
 * generic passthrough would let any app on the phone make the server generate
 * a plan by firing an intent. Mapping only through `pushRoute` guarantees it
 * cannot. Do not widen this into a search-param passthrough.
 */

/** The keys `pushRoute` reads (`route.ts`'s `pushData`). Nothing else passes. */
const LINK_KEYS = ["screen", "sessionId"] as const;

/**
 * What a link names, read from BOTH halves a parser may put it in.
 *
 * A scheme the browser does not know is parsed two ways in the field. Chromium
 * below 130 treats `app.mypocketgym.local://open?screen=…` as an OPAQUE path:
 * `host` is `""` and `pathname` is `//open`. Node and Chromium 130 and up
 * follow the URL Standard and put `open` in `host`, with an empty path. So
 * reading `host` alone dropped EVERY deep link on the older parser — and
 * because a dropped link lands on Today by design, it did it with no error,
 * no log line and a screen that looks right. Observed on the container's
 * WebView (Chromium 128) on 2026-09-21: `screen=session-summary` opened Today.
 *
 * The first segment is the whole comparison, in both shapes, so a foreign host
 * (`…://evil?…`) and a foreign path (`…://evil/open?…`) are refused either way.
 * The opaque path must still carry the `//`: `scheme:open?…` names no
 * authority and is not a link under either parser.
 */
export function linkTarget(parsed: Pick<URL, "host" | "pathname">): string {
  if (parsed.host !== "") {
    return parsed.host;
  }
  if (!parsed.pathname.startsWith("//")) {
    return "";
  }
  return parsed.pathname.slice(2).split("/")[0] ?? "";
}

/**
 * The push `data` a link carries. Never throws: an unparseable URL, a foreign
 * host, or no parameters at all each return `{}`, which `pushRoute` routes to
 * Today.
 */
export function dataFromLink(url: string): Record<string, string> {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return {};
  }
  if (linkTarget(parsed) !== "open") {
    return {};
  }
  const data: Record<string, string> = {};
  for (const key of LINK_KEYS) {
    const value = parsed.searchParams.get(key);
    if (value !== null) {
      data[key] = value;
    }
  }
  return data;
}
